import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';

// Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css';

// Tailwind css
import './tailwind.css';

// Router
import { RouterProvider } from 'react-router-dom';
import router from './router/index';

// Redux
import { Provider } from 'react-redux';
import store from './store/index';

// TanStack Query
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient, queryPersister, QUERY_CACHE_VERSION } from './lib/queryClient';

// PWA
import { setupPWA } from './pwa';

// Observability
import { initSentry } from './lib/sentry';
import { AppErrorBoundary } from './components/ErrorBoundary';
import { setupChunkReload } from './lib/chunkReload';

initSentry();
setupPWA();
setupChunkReload();

/** Namespaces de query key que sobrevivem a um reload (ver lib/queryClient.ts). */
const PERSISTED_NAMESPACES = ['bi', 'titulos', 'me', 'elite', 'apps'];

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Suspense>
            <Provider store={store}>
                <PersistQueryClientProvider
                    client={queryClient}
                    persistOptions={{
                        persister: queryPersister,
                        maxAge: 24 * 60 * 60_000,
                        buster: QUERY_CACHE_VERSION,
                        dehydrateOptions: {
                            shouldDehydrateQuery: (query) => {
                                if (query.state.status !== 'success') return false;
                                const [namespace, area] = query.queryKey;
                                if (!PERSISTED_NAMESPACES.includes(String(namespace ?? ''))) return false;
                                // Telas de administração mudam por ação de quem está nelas — não vale guardar.
                                return area !== 'admin';
                            },
                        },
                    }}
                >
                    <AppErrorBoundary>
                        <RouterProvider router={router} />
                    </AppErrorBoundary>
                    {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
                </PersistQueryClientProvider>
            </Provider>
        </Suspense>
    </React.StrictMode>,
);
